import { useEffect, useState } from "react";

/** How many characters of a long id are shown; the rest is in its tooltip and accessible name. */
export const ID_SHOWN = 8;

/** The id as shown: its first characters and an ellipsis, or the whole of a short one. */
export function shortId(id: string): string {
  return id.length > ID_SHOWN + 1 ? `${id.slice(0, ID_SHOWN)}…` : id;
}

/** How long the copy button says "Copied". */
const COPIED_MS = 1500;

/**
 * An id (a run's, a quote's, a segment's, a session's), shortened to its first characters in
 * monospace, with the full id on hover and to assistive technology, and a button that copies it.
 * With `href`, the id links there; the link is to the full id, as before.
 */
export function Id({ id, href }: { id: string; href?: string | undefined }) {
  const short = shortId(id);
  const text =
    short === id ? (
      id
    ) : (
      <>
        <span aria-hidden="true">{short}</span>
        <span className="sr-only">{id}</span>
      </>
    );
  return (
    <span className="inline-flex items-baseline gap-1 whitespace-nowrap">
      {href ? (
        <a href={href} title={id}>
          <code>{text}</code>
        </a>
      ) : (
        <code title={id}>{text}</code>
      )}
      <CopyButton text={id} />
    </span>
  );
}

/** A button that copies a text, saying so for a moment after. */
export function CopyButton({ text, label = "Copy id" }: { text: string; label?: string }) {
  const [state, setState] = useState<"copied" | "failed">();
  useEffect(() => {
    if (!state) return;
    const timer = setTimeout(() => setState(undefined), COPIED_MS);
    return () => clearTimeout(timer);
  }, [state]);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setState("copied");
    } catch {
      setState("failed");
    }
  };
  return (
    <>
      <button
        type="button"
        aria-label={label}
        title={state === "copied" ? "Copied" : label}
        onClick={copy}
        className="inline-grid size-[18px] flex-none cursor-pointer place-items-center self-center rounded-sm text-muted hover:bg-hover hover:text-fg"
      >
        <svg
          viewBox="0 0 16 16"
          width="12"
          height="12"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
          focusable="false"
        >
          {state === "copied" ? (
            <path d="m3.5 8.5 3 3 6-7" />
          ) : (
            <>
              <rect x="5.25" y="5.25" width="8" height="8" rx="1.5" />
              <path d="M10.75 5.25v-1.5a1.5 1.5 0 0 0-1.5-1.5h-5a1.5 1.5 0 0 0-1.5 1.5v5a1.5 1.5 0 0 0 1.5 1.5h1.5" />
            </>
          )}
        </svg>
      </button>
      {state && (
        <span role="status" className={state === "copied" ? "text-ok text-xs" : "text-bad text-xs"}>
          {state === "copied" ? "Copied" : "Not copied"}
        </span>
      )}
    </>
  );
}
